// apps/web/src/components/content/ContentManager.tsx
// Content management UI component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { ContentItem } from '../../types/content';
import { apiClient } from '../../lib/apiClient';

interface ContentManagerProps {
  tenantId?: string;
}

export function ContentManager(_props: ContentManagerProps) {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/content');
        setContentItems(response.data.content);
      } catch (err) {
        setError('Failed to load content');
        console.error('Error loading content:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const handleCreateContent = async (contentData: any) => {
    try {
      const response = await apiClient.post('/api/v1/content', contentData);
      setContentItems([...contentItems, response.data]);
      setShowForm(false);
    } catch (err) {
      setError('Failed to create content');
      console.error('Error creating content:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading content...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="content-manager">
      <div className="header">
        <h2>Content Management</h2>
        <button 
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : 'Add Content'}
        </button>
      </div>

      {showForm && (
        <ContentForm 
          onSubmit={handleCreateContent} 
          onCancel={() => setShowForm(false)} 
        />
      )}

      <div className="content-grid">
        {contentItems.map(item => (
          <ContentCard key={item.id} content={item} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function ContentCard({ content }: { content: ContentItem }) {
  return (
    <div className="content-card">
      <h3>{content.title}</h3>
      <p>{content.description}</p>
      <p>Type: {content.contentType}</p>
      <p>Size: {content.fileSize ? `${(content.fileSize / 1024).toFixed(1)} KB` : 'N/A'}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Edit</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function ContentForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    contentType: 'IMAGE',
    isPublic: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="content-form">
      <h3>Create New Content</h3>
      <input
        type="text"
        placeholder="Title"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <select
        value={formData.contentType}
        onChange={(e) => setFormData({...formData, contentType: e.target.value})}
      >
        <option value="IMAGE">Image</option>
        <option value="VIDEO">Video</option>
        <option value="DOCUMENT">Document</option>
        <option value="AUDIO">Audio</option>
        <option value="OTHER">Other</option>
      </select>
      <label>
        <input
          type="checkbox"
          checked={formData.isPublic}
          onChange={(e) => setFormData({...formData, isPublic: e.target.checked})}
        />
        Public Access
      </label>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}